import { styled, Theme, CSSObject, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import GpanelLogo from '../../assets/gpanel-logo-dark.png';
import GpanelLogoWhite from '../../assets/gpanel-logo.png';
import GpanelLogoBlock from '../../assets/gpanel-logo-block.png';
import GpanelLogoBlockWhite from '../../assets/gpanel-logo-block-white.png';
import NavLinks from './nav-links';
import { useStore } from '../state/zustand-store';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  createAdminLoggedInLog,
  saveViewType,
  UserKeys,
} from '../../api/users-api';
import { useQueryClient } from '@tanstack/react-query';
import MarketingBar from '../view-admin/marketing-bar';
import { useUserSettings } from '../../utils/use-user-settings';
import TopNav from '../../shared/top-nav';
import { UserSettings } from '../../../types';
import { userPortalPagesConfig } from '../../shared/user-portal-pages-config';
import { useState, useEffect, useRef, useMemo } from 'react';
import { darkTheme, lightTheme } from '../../theme/theme';
import EnterpriseUpgradeCard from '../view-admin/enterprise-upgrade-card';

import Stack from '@mui/material/Stack';
import KeyboardDoubleArrowLeftOutlined from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlined from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';

const drawerWidth = 250;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  paddingLeft: 4,
  paddingRight: 4,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  paddingLeft: 2,
  paddingRight: 2,
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  '& .logo-container': {
    position: 'relative',
    width: '200px',
    height: '50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  '& .logo-image': {
    position: 'absolute',
    transition: theme.transitions.create(['width', 'opacity'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&.expanded': {
      width: '175px',
      opacity: 1,
    },
    '&.collapsed': {
      width: '45px',
      opacity: 1,
    },
  },
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export default function AdminLayout() {
  const { marketingOverview, viewType, roleType } = useUserSettings();
  const collapsedDrawerWidth = 80;
  const buttonSize = 28;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(viewType.expandedNav);
  const hasCreatedAdminLoggedInLog = useRef(false);
  const location = useLocation();

  const { colorMode, setColorMode } = useStore();

  const theme = useMemo(
    () => (colorMode === 'light' ? lightTheme : darkTheme),
    [colorMode]
  );

  useEffect(() => {
    setColorMode(viewType.colorMode);
    setOpen(viewType.expandedNav);
  }, [setColorMode, viewType.colorMode, viewType.expandedNav]);

  function handleOpenButtonClick() {
    setOpen(!open);

    const updatedViewType = {
      ...viewType,
      expandedNav: !open,
    };

    saveViewType(updatedViewType);

    queryClient.setQueryData<UserSettings | undefined>(
      [UserKeys.UserSettings],
      (oldData) => {
        if (!oldData) {
          return;
        }

        return {
          ...oldData,
          viewType: updatedViewType,
        };
      }
    );
  }

  useEffect(() => {
    if (
      roleType === 'admin' &&
      !hasCreatedAdminLoggedInLog.current &&
      !location.pathname.includes(userPortalPagesConfig.userPortal.link)
    ) {
      createAdminLoggedInLog();
      hasCreatedAdminLoggedInLog.current = true;
    }
  }, []);

  if (roleType === 'user') {
    return <Navigate to={userPortalPagesConfig.userPortal.link} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <title>gPanel | Admin Dashboard</title>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          minHeight: '100vh',
          width: '100%',
          bgcolor: theme.palette.background.default,
        }}
      >
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            '& .MuiDrawer-paper': {
              boxShadow: '1px 0px 6px 0px #0000001F',
              border: 'none',
              backgroundColor: theme.palette.semantic.surface.bg1,
            },
          }}
        >
          <DrawerHeader
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pt: 2,
              mb: 1,
            }}
          >
            <div
              className="logo-container"
              onClick={() => navigate('/')}
              style={{
                transform: open ? 'translateX(-12px)' : 'unset',
              }}
            >
              {/* Dark mode expanded logo */}
              <img
                src={GpanelLogoWhite}
                alt="Gpanel Logo"
                className={`logo-image ${open ? 'expanded' : 'collapsed'}`}
                style={{
                  display: colorMode === 'dark' && open ? 'block' : 'none',
                }}
              />
              {/* Light mode expanded logo */}
              <img
                src={GpanelLogo}
                alt="Gpanel Logo"
                className={`logo-image ${open ? 'expanded' : 'collapsed'}`}
                style={{
                  display: colorMode !== 'dark' && open ? 'block' : 'none',
                }}
              />
              {/* Dark mode collapsed logo */}
              <img
                src={GpanelLogoBlockWhite}
                alt="Gpanel Logo"
                className={`logo-image ${open ? 'expanded' : 'collapsed'}`}
                style={{
                  display: colorMode === 'dark' && !open ? 'block' : 'none',
                }}
              />
              {/* Light mode collapsed logo */}
              <img
                src={GpanelLogoBlock}
                alt="Gpanel Logo"
                className={`logo-image ${open ? 'expanded' : 'collapsed'}`}
                style={{
                  display: colorMode !== 'dark' && !open ? 'block' : 'none',
                }}
              />
            </div>
          </DrawerHeader>
          {/* <Divider /> */}
          <NavLinks drawerOpen={open} />
          <EnterpriseUpgradeCard drawerOpen={open} />
        </Drawer>
        <IconButton
          onClick={handleOpenButtonClick}
          sx={{
            position: 'fixed',
            left: open
              ? drawerWidth - 30 - buttonSize / 2
              : collapsedDrawerWidth - 8 - buttonSize / 2,
            top: 38,
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: theme.palette.semantic.surface.bg1,
            '&:hover': {
              bgcolor: theme.palette.semantic.surface.bg2,
            },
            transition: (theme) =>
              theme.transitions.create(['left'], {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            zIndex: (theme) => theme.zIndex.drawer,
            boxShadow: open ? '1px 0px 6px 0px #0000001F' : 'unset',
            borderRight: open ? '1px solid rgba(255, 255, 255, 0.12)' : 'none',
          }}
        >
          {open ? (
            <KeyboardDoubleArrowLeftOutlined sx={{ fontSize: 20 }} />
          ) : (
            <KeyboardDoubleArrowRightOutlined sx={{ fontSize: 20 }} />
          )}
        </IconButton>
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 0,
            flexBasis: 0,
            minWidth: 0,
          }}
        >
          {marketingOverview !== null && (
            <MarketingBar marketingOverview={marketingOverview} />
          )}

          <Box
            component="main"
            sx={{
              pt: 1,
              pb: 2,
              pl: 3,
              pr: 3,
            }}
          >
            <Stack
              component="main"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={2}
              >
                {/* <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleOpenButtonClick}
                  edge="start"
                >
                  <MenuIcon />
                </IconButton> */}
              </Stack>

              <TopNav isUserPortal={false} />
            </Stack>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
